import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  addDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';

// 계좌 인증 요청 이력
// 컬렉션: account_verification_requests
// 상태: requested → (관리자가 1원 송금) → pending → (유저 코드 입력) → succeeded/failed/expired
// level: manual_one_won (현재 운영) / account_owner_inquiry (향후 외부 API)

const COLL = 'account_verification_requests';

const generateCode = () => String(Math.floor(1000 + Math.random() * 9000));

// 유저가 1원 인증 요청 — 계좌정보 + 4자리 코드 생성 + pending 등록
// realtors 문서의 계좌정보는 아직 갱신하지 않는다 (검증 완료 후에만)
export const requestOneWonVerification = async (uid, { bankName, accountNumber, accountHolder, accountType }) => {
  if (!uid) throw new Error('로그인이 필요합니다.');
  if (!bankName || !accountNumber || !accountHolder) {
    throw new Error('은행/계좌번호/예금주는 필수입니다.');
  }
  const code = generateCode();
  const expiresAt = new Date(Date.now() + 24 * 3600 * 1000);
  const docRef = await addDoc(collection(db, COLL), {
    realtor_id: uid,
    provider: 'manual_one_won',
    verification_level: 'manual_one_won',
    status: 'requested',
    bank_name: bankName,
    account_number: accountNumber,
    account_holder: accountHolder,
    account_type: accountType || 'personal',
    verification_code: code,
    expires_at: expiresAt.toISOString(),
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
  return { id: docRef.id, code, expiresAt: expiresAt.toISOString() };
};

// 최신 요청 조회
export const getLatestVerificationRequest = async (uid) => {
  if (!uid) return null;
  const q = query(
    collection(db, COLL),
    where('realtor_id', '==', uid),
    orderBy('created_at', 'desc'),
    limit(1)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
};

// 유저가 입력한 코드 확인 → 일치하면 realtors.accountVerified=true 로 승격
export const confirmOneWonVerification = async (uid, requestId, userCode) => {
  if (!uid || !requestId || !userCode) {
    throw new Error('요청 정보가 누락됐습니다.');
  }
  const reqRef = doc(db, COLL, requestId);
  const reqSnap = await getDoc(reqRef);
  if (!reqSnap.exists()) throw new Error('요청을 찾을 수 없습니다.');
  const data = reqSnap.data();
  if (data.realtor_id !== uid) throw new Error('요청 소유자가 아닙니다.');
  if (data.status === 'succeeded') return { alreadyVerified: true };
  if (data.expires_at && new Date(data.expires_at).getTime() < Date.now()) {
    await updateDoc(reqRef, { status: 'expired', updated_at: serverTimestamp() });
    throw new Error('인증 요청이 만료됐습니다. 다시 요청해주세요.');
  }
  if (String(userCode).trim() !== String(data.verification_code)) {
    await updateDoc(reqRef, { status: 'failed', failure_reason: 'code_mismatch', updated_at: serverTimestamp() });
    throw new Error('입금자명에 찍힌 4자리 숫자와 일치하지 않습니다.');
  }
  // 성공 → realtors 문서에 계좌정보 + 인증 플래그 반영
  await updateDoc(doc(db, 'realtors', uid), {
    bank_name: data.bank_name,
    account_number: data.account_number,
    account_holder: data.account_holder,
    account_type: data.account_type || 'personal',
    accountVerified: true,
    account_verified: true,
    verified_at: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  await updateDoc(reqRef, {
    status: 'succeeded',
    verified_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
  return { success: true };
};

// 관리자용: 1원 송금 완료 표시 (pending 상태로 이동) — 선택적
export const markOneWonSent = async (requestId) => {
  await updateDoc(doc(db, COLL, requestId), {
    status: 'pending',
    one_won_sent_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
};
